import { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { getChildren, createChild } from '../../api';
import './Enfants.css';

function Enfants() {
  const { token } = useAuth();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
  });
  const [formError, setFormError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchChildren();
  }, [token]);

  const fetchChildren = async () => {
    try {
      const response = await getChildren(token);
      setChildren(response.data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des enfants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormErrors({});
    setSubmitting(true);

    try {
      await createChild(token, formData);
      setShowModal(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        password_confirmation: '',
      });
      fetchChildren();
    } catch (err) {
      setFormError(err.message || 'Une erreur est survenue');
      if (err.errors) {
        setFormErrors(err.errors);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openModal = () => {
    setFormError('');
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
    });
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (loading) {
    return (
      <div className="enfants-page">
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="enfants-page">
      <header className="enfants-header">
        <h1>Mes enfants</h1>
        <button className="add-enfant-button" onClick={openModal}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
          <span>Ajouter un enfant</span>
        </button>
      </header>

      <main className="enfants-content">
        {children.length === 0 ? (
          <div className="enfants-empty">
            <svg viewBox="0 0 24 24" fill="currentColor" className="enfants-empty-icon">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
            <p>Vous n'avez pas encore ajouté d'enfant.</p>
            <p className="enfants-empty-hint">Cliquez sur "Ajouter un enfant" pour commencer.</p>
          </div>
        ) : (
          <div className="enfants-grid">
            {children.map(child => (
              <div key={child.id} className="enfant-card">
                <div className="enfant-avatar">
                  {getInitials(child.first_name, child.last_name)}
                </div>
                <div className="enfant-info">
                  <h3>{child.first_name} {child.last_name}</h3>
                  <p>{child.email}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ajouter un enfant</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              {formError && <div className="form-error">{formError}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">Prénom *</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                  {formErrors.first_name && <span className="field-error">{formErrors.first_name[0]}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="last_name">Nom *</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                  />
                  {formErrors.last_name && <span className="field-error">{formErrors.last_name[0]}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                {formErrors.email && <span className="field-error">{formErrors.email[0]}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Téléphone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
                {formErrors.phone && <span className="field-error">{formErrors.phone[0]}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Mot de passe *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                  />
                  {formErrors.password && <span className="field-error">{formErrors.password[0]}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="password_confirmation">Confirmer *</label>
                  <input
                    type="password"
                    id="password_confirmation"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Création...' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Enfants;
